<template>
  <tabs />
  <div class="main-area h-full flex flex-col">
    <div class="flex gap-4 p-2">
      <Multiselect
        v-model="region"
        :options="regions"
        :loading="loadingRegions"
        :label="$t('visibility.planning.region')"
        class="min-w-[200px] p-2 max-w-[400px] w-[300px]"
        :can-deselect="false"
        :can-clear="true"
        :searchable="true"
      />
    </div>
    <v-chart
      v-if="!loading"
      ref="chartRef"
      :option="chartOptions"
      class="w-full h-full pr-20"
      @click="handleChartClick"
    />
    <div
      v-else
      style="display: flex; justify-content: center; align-items: center; padding: 10rem; margin-left: 3rem;"
    >
      <Spinner />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref, computed, unref, type Ref, reactive, watchEffect, watch,
} from 'vue';
import { use } from 'echarts/core';
import VChart from 'vue-echarts';
import { CustomChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {
  useProvider,
  useProviderOpt,
} from '@utils/table/hooks/provider';
import { addDays, format } from 'date-fns';
import { useQueryRef } from '@hooks/url';
import Multiselect from '@utils/Multiselect.vue';
import Spinner from '@utils/Spinner.vue';
import type {
  Region,
  VisibilityBicycle,
} from '@/entities/Entities';
import router from '@/router';

use([CustomChart, GridComponent, TooltipComponent, CanvasRenderer]);

const region = ref<Region>();

const {
  data: regions,
  loading: loadingRegions,
} = useProviderOpt<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value: Region) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = computed(() => ({
  region: region.value,
}));

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const chartRef = ref(null);

const { data: visibilityData, loading } = useProvider<VisibilityBicycle>('/visibility_bicycles/usage', providerParams);

const records = computed<VisibilityBicycle[]>(() => unref(visibilityData)?.['hydra:member']);
const plannings: Ref<{ bicycle: string; start: string | Date; end: string | Date }[]> = ref([]);
const bicycles = ref<string[]>([]);
const maxDateForGraph = ref(new Date());

const formattedData = computed(() => {
  if (!records.value) return { plannings: [], bicycles: [], maxDate: new Date() };

  let maxDate = 0;
  const newPlannings = [];
  const newBicycles = new Set<string>();

  for (const bicycle of records.value) {
    const bicycleLabel = `${bicycle.region?.name} ${bicycle.number}`;
    newBicycles.add(bicycleLabel);
    for (const planning of bicycle.visibilityPlannings) {
      if (planning?.endTime) {
        const endTimeMs = new Date(planning.endTime).getTime();
        if (endTimeMs > maxDate) {
          maxDate = endTimeMs;
        }
      }
      newPlannings.push({
        bicycle: bicycleLabel,
        start: planning.startTime ?? '',
        end: planning.endTime ?? '',
        planning,
      });
    }
  }

  return {
    plannings: newPlannings,
    bicycles: Array.from(newBicycles),
    maxDate: new Date(maxDate),
  };
});

watchEffect(() => {
  const { plannings: newPlannings, bicycles: newBicycles, maxDate } = formattedData.value;
  plannings.value = newPlannings;
  bicycles.value = newBicycles;
  maxDateForGraph.value = maxDate;
});

const parseDate = (dateString: string) => new Date(dateString).getTime();
const xAxisMin = addDays(new Date(), -14).getTime();
const xAxisMax = addDays(new Date(), 60).getTime();

const handleLabelClick = (bicycleDesignation: string) => {
  const [, ...numberParts] = bicycleDesignation.trim().split(/\s+/);
  const number = numberParts.join(' ');

  const routeData = router.resolve({
    name: 'bicycles',
    query: {
      filter: encodeURIComponent(JSON.stringify({
        number,
      })),
    },
  });
  window.open(routeData.href, '_blank');
};

const handleChartClick = (params) => {
  // handle when a click on the chart itself happens (not y-axis label)
  if (params.seriesType === 'custom') {
    const planningIndex = params.value[3];
    const planning = plannings.value?.[planningIndex]?.planning;
    const routeData = router.resolve({
      name: 'plannings-confirmed',
      query: {
        filter: encodeURIComponent(JSON.stringify({
          id: planning?.id,
        })),
      },
    });
    window.open(routeData.href, '_blank');
  }
};

const chartOptions = computed(() => ({
  tooltip: {
    trigger: 'item',
    formatter: (params: any) => {
      const formatDateTime = (timestamp: string) => format(new Date(timestamp), 'dd.MM.yyyy - HH:mm:ss');

      const durationInDays = Math.round(params.value[1] / (1000 * 60 * 60 * 24));

      const planningIndex = params.value[3];
      const planning = plannings.value?.[planningIndex]?.planning;
      const label = planning?.newDesign ?? '';

      return `${params.name}<br/>
          Start: ${formatDateTime(planning?.startTime)}<br/>
          End: ${formatDateTime(planning?.endTime)}<br/>
          Duration: ${durationInDays} days<br/>
          Design: ${label}`;
    },
  },
  grid: {
    left: '5%',
    right: '5%',
    bottom: '10%',
    containLabel: true,
  },
  xAxis: {
    type: 'time',
    min: addDays(new Date(), -14),
    max: addDays(new Date(), 60),
    axisLabel: { rotate: 45 },
  },
  yAxis: {
    type: 'category',
    data: bicycles.value.map((bicycle) => ({
      value: bicycle,
      textStyle: { color: '#007BFF', cursor: 'pointer', fontWeight: 'bold' },
    })),
    triggerEvent: true,
    axisLabel: {
      formatter: (value: string) => `{clickable|${value}}`,
      rich: {
        clickable: {
          color: '#007BFF',
          fontWeight: 'bold',
          fontSize: 14,
          textDecoration: 'underline',
        },
      },
    },
  },
  series: [
    {
      type: 'custom',
      renderItem: (params, api) => {
        const start = api.value(0);
        const duration = api.value(1);
        const categoryIndex = api.value(2);
        const rectX = api.coord([start, categoryIndex])[0];
        const rectY = api.coord([0, categoryIndex])[1] - 10;
        const rectWidth = api.size([duration, 0])[0];
        const rectHeight = 20;

        const planningIndex = api.value(3);
        let label = plannings.value?.[planningIndex]?.planning?.newDesign ?? '';

        const maxLength = 10;
        if (label.length > maxLength) {
          label = `${label.slice(0, maxLength)}…`;
        }

        const rectShape = {
          type: 'rect',
          shape: {
            x: rectX,
            y: rectY,
            width: rectWidth,
            height: rectHeight,
          },
          style: {
            fill: '#4CAF50',
            stroke: '#000',
            lineWidth: 1,
            cursor: 'pointer',
          },
        };
        const textShape = {
          type: 'text',
          style: {
            text: label,
            x: rectX + rectWidth / 2,
            y: rectY + rectHeight / 2,
            fill: '#fff',
            textAlign: 'center',
            textVerticalAlign: 'middle',
          },
        };

        return {
          type: 'group',
          children: [rectShape, textShape],
        };
      },
      data: formattedData.value.plannings?.map((entry, index) => {
        const start = parseDate(entry.start);
        const end = parseDate(entry.end);
        const clippedStart = Math.max(start, xAxisMin);
        const clippedEnd = Math.min(end, xAxisMax);
        const duration = clippedEnd - clippedStart;

        if (clippedEnd <= xAxisMin || clippedStart >= xAxisMax || duration <= 0) return null;

        return [
          clippedStart,
          duration,
          bicycles.value.indexOf(entry.bicycle),
          index,
        ];
      }).filter(Boolean),
    },
  ],
}));

watch([loading, chartRef], () => {
  // handle when a click happens on the y-axis
  if (!loading.value && chartRef.value?.chart) {
    chartRef.value.chart.on('click', (params: any) => {
      if (params.targetType === 'axisLabel' && params.componentType === 'yAxis') {
        handleLabelClick(params.value);
      }
    });
  }
});
</script>

<style scoped>
button {
  @apply block;
}
</style>
